"use client";

import { useState } from "react";
import {
  Calendar,
  Clock,
  Globe,
  GraduationCap,
  Brain,
  Cpu,
  BotIcon as Robot,
  Zap,
} from "lucide-react";
import Footer from "../Header/Footer";
import Navbar from "../Header/Navbar";
import ApplyButton from "../common/ApplyButton";

const BsArtificialIntelligencePage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [showAllSemesters, setShowAllSemesters] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="font-sans">
      {/* Header */}
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-teal-800 to-teal-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                Bachelor of Science in Artificial Intelligence
              </h1>
              <p className="text-xl mb-6 text-teal-100">
                Shape the future with cutting-edge AI technologies
              </p>
              <div className="flex flex-wrap gap-4">
                <ApplyButton
                  to="/login"
                  className="bg-white text-teal-700 hover:bg-teal-50 font-bold py-3 px-6 rounded-lg transition duration-300 shadow-lg transform hover:scale-105"
                >
                  Apply Now
                </ApplyButton>
              </div>
            </div>
            <div
              className="md:w-1/2"
              style={{ height: "30rem", width: "43rem" }}
            >
              <img
                style={{ objectFit: "cover", height: "100%", width: "100%" }}
                src="https://images.unsplash.com/photo-1555255707-c07966088b7b?q=80&w=2036&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                alt="BS Artificial Intelligence Students"
                className="rounded-lg shadow-2xl transform hover:scale-105 transition duration-300 w-200 h-200 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info */}
      <section className="bg-gray-100 py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg transform hover:scale-105 transition duration-300">
              <Calendar className="text-teal-600 mb-4" size={48} />
              <h3 className="font-semibold text-gray-800 text-xl mb-2">
                Program Duration
              </h3>
              <p className="text-gray-600">4 Years (8 Semesters)</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg transform hover:scale-105 transition duration-300">
              <GraduationCap className="text-teal-600 mb-4" size={48} />
              <h3 className="font-semibold text-gray-800 text-xl mb-2">
                Degree Awarded
              </h3>
              <p className="text-gray-600">BS in Artificial Intelligence</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg transform hover:scale-105 transition duration-300">
              <Clock className="text-teal-600 mb-4" size={48} />
              <h3 className="font-semibold text-gray-800 text-xl mb-2">
                Credit Hours
              </h3>
              <p className="text-gray-600">138 Credit Hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Program Tabs */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-4">
              Program Details
            </h2>
            <p className="text-center text-gray-600 max-w-5xl mx-auto text-lg">
              The Bachelor of Science in Artificial Intelligence (BSAI) at NIIT,
              Lahore, is a 4-year, HEC-recognized undergraduate program
              comprising 130 credit hours over 8 semesters. The program focuses
              on developing intelligent systems using core concepts in machine
              learning, neural networks, robotics, and natural language
              processing. Students are equipped with both theoretical
              understanding and practical skills through hands-on labs, research
              projects, and industrial exposure. The curriculum is aligned with
              global AI advancements to prepare graduates for future-driven
              careers.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center border-b border-gray-200 mb-8">
            {["overview", "curriculum", "careers"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 px-6 font-medium text-sm focus:outline-none transition duration-300 ${
                  activeTab === tab
                    ? "border-b-2 border-teal-600 text-teal-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white p-8 rounded-lg shadow-lg">
            {activeTab === "overview" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Program Overview
                </h3>
                <p className="text-gray-700 mb-6">
                  The BSAI program is designed to produce AI professionals with
                  deep expertise in intelligent algorithms, data-driven
                  decision-making, and system automation. It blends core
                  computer science foundations with specialized AI domains to
                  address real-world challenges through innovative solutions.
                  The program fosters both technical proficiency and research
                  capability for roles in AI development, data science, and
                  intelligent automation.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {[
                    {
                      icon: <Brain className="text-teal-600" size={36} />,
                      title: "Cutting-edge AI Curriculum",
                      description:
                        "Learn the latest AI frameworks, algorithms, and computational models aligned with emerging industry demands.",
                    },
                    {
                      icon: <Robot className="text-teal-600" size={36} />,
                      title: "Hands-on Projects",
                      description:
                        "Build AI-driven applications and systems using state-of-the-art tools and technologies in dedicated AI labs.",
                    },
                    {
                      icon: <Cpu className="text-teal-600" size={36} />,
                      title: "Industry Collaboration",
                      description:
                        "Gain real-world experience through partnerships with top tech firms on live projects and internships.",
                    },
                    {
                      icon: <Globe className="text-teal-600" size={36} />,
                      title: "Research Opportunities",
                      description:
                        "Engage in supervised AI research projects and contribute to advancements in intelligent system design.",
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start bg-gray-50 p-6 rounded-lg transform hover:scale-105 transition duration-300"
                    >
                      <div className="bg-white p-3 rounded-full mr-4 shadow-md">
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 text-lg mb-2">
                          {item.title}
                        </h4>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "curriculum" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Curriculum
                </h3>
                <p className="text-gray-700 mb-6">
                  Our BS Artificial Intelligence curriculum provides a strong
                  foundation in computer science with specialized knowledge in
                  AI principles, machine learning, and intelligent systems. The
                  program is structured over 8 semesters, with a total of 138
                  credit hours.
                </p>

                <div className="mb-8">
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Course Structure
                  </h4>

                  {[
                    // Semester 1
                    {
                      semester: 1,
                      courses: [
                        {
                          sNo: 1,
                          code: "CS111",
                          title: "Programming Fundamentals",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 2,
                          code: "CS111L",
                          title: "Programming Fundamentals Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 3,
                          code: "CS181",
                          title:
                            "Application of Information and Communication Technologies",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 4,
                          code: "CS181L",
                          title:
                            "Application of Information and Communication Technologies Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 5,
                          code: "HU124",
                          title: "Islamic Studies and Ethics",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 6,
                          code: "EL170",
                          title: "Functional English",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 7,
                          code: "PH111",
                          title: "Applied Physics",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 8,
                          code: "PH111L",
                          title: "Applied Physics Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 9,
                          code: "MA114",
                          title: "Foundational Mathematics (Non-Credit)",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                      ],
                      totalTheory: 12,
                      totalLab: 3,
                      totalCredits: 15,
                    },
                    // Semester 2
                    {
                      semester: 2,
                      courses: [
                        {
                          sNo: 1,
                          code: "CS112",
                          title: "Object Oriented Programming",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 2,
                          code: "CS112L",
                          title: "Object Oriented Programming Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 3,
                          code: "CS130",
                          title: "Database Systems",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 4,
                          code: "CS130L",
                          title: "Database Systems Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 5,
                          code: "EE123",
                          title: "Digital Logic Design",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 6,
                          code: "EE123L",
                          title: "Digital Logic Design Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 7,
                          code: "CS305",
                          title: "Software Engineering",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 8,
                          code: "MA216",
                          title: "Discrete Structures",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 9,
                          code: "MA118",
                          title: "Pre-Calculus (Non-Credit)",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                      ],
                      totalTheory: 14,
                      totalLab: 3,
                      totalCredits: 17,
                    },
                    // Semester 3
                    {
                      semester: 3,
                      courses: [
                        {
                          sNo: 1,
                          code: "CS216",
                          title: "Data Structures",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 2,
                          code: "CS216L",
                          title: "Data Structures Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 3,
                          code: "AI230",
                          title: "Programming for Artificial Intelligence",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 4,
                          code: "AI230L",
                          title: "Programming for Artificial Intelligence Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 5,
                          code: "CS260",
                          title: "Computer Networks",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 6,
                          code: "CS260L",
                          title: "Computer Networks Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 7,
                          code: "MA110",
                          title: "Calculus and Analytical Geometry",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 8,
                          code: "MA301",
                          title: "Probability and Statistics",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                      ],
                      totalTheory: 13,
                      totalLab: 3,
                      totalCredits: 16,
                    },
                    // Semester 4
                    {
                      semester: 4,
                      courses: [
                        {
                          sNo: 1,
                          code: "CS226",
                          title: "Computer Organization and Assembly Language",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 2,
                          code: "CS226L",
                          title:
                            "Computer Organization and Assembly Language Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 3,
                          code: "CS344",
                          title: "Artificial Intelligence",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 4,
                          code: "CS344L",
                          title: "Artificial Intelligence Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 5,
                          code: "AIXXX",
                          title: "Domain Elective-I",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 6,
                          code: "AIXXXL",
                          title: "Domain Elective-I Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 7,
                          code: "AI233",
                          title: "Machine Learning",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 8,
                          code: "AI233L",
                          title: "Machine Learning Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 9,
                          code: "MA201",
                          title: "Linear Algebra",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 10,
                          code: "MA105",
                          title: "Multivariable Calculus",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                      ],
                      totalTheory: 14,
                      totalLab: 4,
                      totalCredits: 18,
                    },
                    // Semester 5
                    {
                      semester: 5,
                      courses: [
                        {
                          sNo: 1,
                          code: "CS325",
                          title: "Operating Systems",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 2,
                          code: "CS325L",
                          title: "Operating Systems Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 3,
                          code: "AI321",
                          title: "Knowledge Representation and Reasoning",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 4,
                          code: "AI321L",
                          title: "Knowledge Representation and Reasoning Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 5,
                          code: "AI335",
                          title: "Deep Learning",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 6,
                          code: "AI335L",
                          title: "Deep Learning Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 7,
                          code: "AIXXX",
                          title: "Domain Elective-II",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 8,
                          code: "AIXXXL",
                          title: "Domain Elective-II Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 9,
                          code: "CS332",
                          title: "Design and Analysis of Algorithms",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 10,
                          code: "EL271",
                          title: "Expository Writing",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                      ],
                      totalTheory: 14,
                      totalLab: 4,
                      totalCredits: 18,
                    },
                    // Semester 6
                    {
                      semester: 6,
                      courses: [
                        {
                          sNo: 1,
                          code: "AI303",
                          title: "Computer Vision",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 2,
                          code: "AI303L",
                          title: "Computer Vision Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 3,
                          code: "AIXXX",
                          title: "Domain Elective-III",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 4,
                          code: "AIXXXL",
                          title: "Domain Elective-III Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 5,
                          code: "EL400",
                          title: "Technical and Business Writing",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 6,
                          code: "AIXXX",
                          title: "Domain Elective-IV",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 7,
                          code: "AIXXXL",
                          title: "Domain Elective-IV Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 8,
                          code: "AIXXX",
                          title: "Domain Elective-V",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 9,
                          code: "AIXXXL",
                          title: "Domain Elective-V Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 10,
                          code: "BA353",
                          title: "Entrepreneurship",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 11,
                          code: "AI497",
                          title: "Final Year Project - I",
                          theory: 1,
                          lab: 0,
                          total: 1,
                        },
                      ],
                      totalTheory: 14,
                      totalLab: 4,
                      totalCredits: 18,
                    },
                    // Semester 7
                    {
                      semester: 7,
                      courses: [
                        {
                          sNo: 1,
                          code: "AI498",
                          title: "Final Year Project - II",
                          theory: 0,
                          lab: 2,
                          total: 2,
                        },
                        {
                          sNo: 2,
                          code: "CS215",
                          title: "Information Security",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 3,
                          code: "CS215L",
                          title: "Information Security Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 4,
                          code: "AIXXX",
                          title: "Domain Elective-VI",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 5,
                          code: "AIXXXL",
                          title: "Domain Elective-VI Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 6,
                          code: "AIXXX",
                          title: "Domain Elective-VII",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 7,
                          code: "AIXXXL",
                          title: "Domain Elective-VII Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                        {
                          sNo: 8,
                          code: "CS426",
                          title: "Parallel and Distributed Computing",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 9,
                          code: "CS426L",
                          title: "Parallel and Distributed Computing Lab",
                          theory: 0,
                          lab: 1,
                          total: 1,
                        },
                      ],
                      totalTheory: 8,
                      totalLab: 6,
                      totalCredits: 14,
                    },
                    // Semester 8
                    {
                      semester: 8,
                      courses: [
                        {
                          sNo: 1,
                          code: "AI499",
                          title: "Final Year Project-III",
                          theory: 0,
                          lab: 3,
                          total: 3,
                        },
                        {
                          sNo: 2,
                          code: "HU405",
                          title: "Ideology and Constitution of Pakistan",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 3,
                          code: "CS470",
                          title: "Professional Practices",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 4,
                          code: "HU411",
                          title: "Civics and Community Engagement",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                        {
                          sNo: 5,
                          code: "MK101",
                          title: "Principles of Marketing",
                          theory: 3,
                          lab: 0,
                          total: 3,
                        },
                        {
                          sNo: 6,
                          code: "BA152",
                          title: "Introduction to Management",
                          theory: 2,
                          lab: 0,
                          total: 2,
                        },
                      ],
                      totalTheory: 11,
                      totalLab: 3,
                      totalCredits: 14,
                    },
                  ]
                    // Filter to show only first 2 semesters if showAllSemesters is false
                    .filter(
                      (semester) => showAllSemesters || semester.semester <= 2
                    )
                    .map((semester) => (
                      <div key={semester.semester} className="mb-8">
                        <h5 className="text-lg font-semibold text-teal-700 mb-3">
                          Semester {semester.semester}
                        </h5>
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                                  S No
                                </th>
                                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                                  Course Code
                                </th>
                                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-b">
                                  Course Title
                                </th>
                                <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                                  Theory
                                </th>
                                <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                                  Lab
                                </th>
                                <th className="py-3 px-4 text-center text-sm font-medium text-gray-700 border-b">
                                  Total
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {semester.courses.map((course) => (
                                <tr
                                  key={course.sNo}
                                  className="hover:bg-gray-50"
                                >
                                  <td className="py-2 px-4 border-b text-sm">
                                    {course.sNo}
                                  </td>
                                  <td className="py-2 px-4 border-b text-sm">
                                    {course.code}
                                  </td>
                                  <td className="py-2 px-4 border-b text-sm">
                                    {course.title}
                                  </td>
                                  <td className="py-2 px-4 border-b text-sm text-center">
                                    {course.theory}
                                  </td>
                                  <td className="py-2 px-4 border-b text-sm text-center">
                                    {course.lab}
                                  </td>
                                  <td className="py-2 px-4 border-b text-sm text-center">
                                    {course.total}
                                  </td>
                                </tr>
                              ))}
                              <tr className="bg-teal-50 font-medium">
                                <td
                                  className="py-2 px-4 border-b text-sm"
                                  colSpan="3"
                                >
                                  Total Credit Hours
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  {semester.totalTheory}
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  {semester.totalLab}
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-center">
                                  {semester.totalCredits}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}

                  {!showAllSemesters && (
                    <div className="flex justify-center mt-8">
                      <button
                        onClick={() => setShowAllSemesters(true)}
                        className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-6 rounded-lg transition duration-300 shadow-md"
                      >
                        Show More Semesters
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-8 bg-teal-50 p-6 rounded-lg shadow-md">
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Program Summary
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h5 className="font-medium text-gray-800 mb-2">
                        Total Credit Hours
                      </h5>
                      <ul className="list-disc pl-5 space-y-1 text-gray-700">
                        <li>Theory Courses: 100 credit hours</li>
                        <li>Lab Courses: 30 credit hours</li>
                        <li>Total: 130 credit hours</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-800 mb-2">
                        Course Distribution
                      </h5>
                      <ul className="list-disc pl-5 space-y-1 text-gray-700">
                        <li>Core AI Courses: 20 courses</li>
                        <li>Computer Science Foundation: 15 courses</li>
                        <li>Domain Elective Courses: 14 courses</li>
                        <li>General Education: 12 courses</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-gray-600">
                    <p>
                      * 3 Credit hours internship (Student can apply for 6-8
                      weeks supervised internship course)
                    </p>
                    <p>* Applicable from Fall 2025 Intake & Onwards</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "admission" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Admission Requirements
                </h3>
                <p className="text-gray-700 mb-6">
                  To be eligible for admission to the BS Artificial Intelligence
                  program, applicants must meet the following requirements:
                </p>

                <div className="space-y-8">
                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Academic Requirements
                    </h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                      <li>
                        Higher Secondary School Certificate (HSSC) or equivalent
                      </li>
                      <li>
                        Strong background in Mathematics and Computer Science
                      </li>
                      <li>Good academic standing throughout academic career</li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Entrance Test
                    </h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                      <li>Qualifying score in the NIIT AI Aptitude Test</li>
                      <li>
                        Test covers Mathematics, Logic, and Basic Programming
                        Concepts
                      </li>
                      <li>Sample test papers available on request</li>
                    </ul>
                  </div>

                  {/* <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Required Documents
                    </h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700">
                      <li>Completed application form</li>
                      <li>
                        Attested copies of academic certificates and transcripts
                      </li>
                      <li>Copy of CNIC or B-Form</li>
                      <li>Four recent passport-sized photographs</li>
                      <li>
                        Character certificate from last attended institution
                      </li>
                    </ul>
                  </div> */}

                  <div className="bg-teal-50 p-6 rounded-lg shadow-md border-l-4 border-teal-500">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Application Process
                    </h4>
                    <ol className="list-decimal pl-5 space-y-2 text-gray-700">
                      <li>Submit online application through the NIIT portal</li>
                      <li>Pay application fee</li>
                      <li>Appear for entrance test on the scheduled date</li>
                      <li>
                        Shortlisted candidates will be called for interview
                      </li>
                      <li>
                        Final selection based on academic record, entrance test,
                        and interview performance
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "careers" && (
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Career Prospects
                </h3>
                <p className="text-gray-700 mb-6">
                  Graduates of our BS Artificial Intelligence program are
                  well-prepared for exciting careers in the rapidly growing
                  field of AI. Our program equips students with the skills and
                  knowledge needed to innovate and lead in various industries
                  leveraging AI technologies.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Job Opportunities
                    </h4>
                    <ul className="space-y-4">
                      {[
                        {
                          icon: <Brain size={20} />,
                          title: "AI Engineer",
                          description:
                            "Design and implement AI algorithms and systems",
                        },
                        {
                          icon: <Cpu size={20} />,
                          title: "Machine Learning Engineer",
                          description:
                            "Develop and deploy machine learning models",
                        },
                        {
                          icon: <Zap size={20} />,
                          title: "Data Scientist",
                          description:
                            "Analyze complex data sets to derive AI-driven insights",
                        },
                        {
                          icon: <Robot size={20} />,
                          title: "Robotics Engineer",
                          description:
                            "Design and develop intelligent robotic systems",
                        },
                      ].map((job, index) => (
                        <li key={index} className="flex items-start">
                          <div className="bg-teal-100 p-2 rounded-full mr-3 mt-1">
                            {job.icon}
                          </div>
                          <div>
                            <span className="font-medium text-gray-800">
                              {job.title}
                            </span>
                            <p className="text-sm text-gray-600">
                              {job.description}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                    <h4 className="text-xl font-semibold text-gray-800 mb-4">
                      Advanced Opportunities
                    </h4>
                    <ul className="space-y-4">
                      {[
                        {
                          icon: <Brain size={20} />,
                          title: "AI Research Scientist",
                          description:
                            "Conduct cutting-edge research in AI and machine learning",
                        },
                        {
                          icon: <Cpu size={20} />,
                          title: "AI Product Manager",
                          description:
                            "Lead the development of AI-powered products",
                        },
                        {
                          icon: <Zap size={20} />,
                          title: "AI Ethics Consultant",
                          description:
                            "Ensure ethical implementation of AI technologies",
                        },
                        {
                          icon: <Robot size={20} />,
                          title: "AI Startup Founder",
                          description: "Launch innovative AI-based startups",
                        },
                      ].map((job, index) => (
                        <li key={index} className="flex items-start">
                          <div className="bg-teal-100 p-2 rounded-full mr-3 mt-1">
                            {job.icon}
                          </div>
                          <div>
                            <span className="font-medium text-gray-800">
                              {job.title}
                            </span>
                            <p className="text-sm text-gray-600">
                              {job.description}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-teal-50 p-6 rounded-lg shadow-md mb-8">
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Industry Sectors
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      "Tech Giants",
                      "Healthcare",
                      "Finance",
                      "Automotive",
                      "E-commerce",
                      "Education",
                      "Manufacturing",
                      "Research Institutions",
                    ].map((sector, index) => (
                      <div
                        key={index}
                        className="bg-white p-3 rounded text-center shadow-sm"
                      >
                        <p className="font-medium text-gray-700">{sector}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    Further Education
                  </h4>
                  <p className="text-gray-700 mb-4">
                    BS Artificial Intelligence graduates can pursue advanced
                    degrees and certifications such as:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li>Master of Science in Artificial Intelligence</li>
                    <li>Master of Science in Machine Learning</li>
                    <li>PhD in Artificial Intelligence or related fields</li>
                    <li>Specialized AI certifications from industry leaders</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-20 bg-gradient-to-r from-teal-800 to-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Lead the AI Revolution?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Join our BS Artificial Intelligence program and develop the skills
            to shape the future of technology and innovation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <ApplyButton
              to="/login"
              className="bg-white text-teal-700 hover:bg-teal-50 font-bold py-3 px-8 rounded-lg transition duration-300 shadow-lg transform hover:scale-105"
            >
              Apply Now
            </ApplyButton>
          </div>
        </div>
      </section> */}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BsArtificialIntelligencePage;
